import {
  get,
  find,
  isArray,
  sortBy,
  isUndefined,
  map,
  each,
  isNil,
  isString,
  groupBy,
  keys,
  includes,
  isNaN,
  omit,
  findIndex,
  round,
  isEmpty,
  every,
  isEqual,
  has,
  maxBy,
  minBy,
  forEach,
  filter,
} from 'lodash';
// makes this easier to mock for our shuffle.test.js
import shuffle from 'lodash/shuffle';
import emojiRegex from 'emoji-regex/RGI_Emoji';

import {
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERICAL_OPEN_ENDED,
  QUESTION_RANKING,
  QUESTION_NPS,
  QUESTION_RATING,
  QUESTION_SLIDER,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_VAN_WESTENDORP,
  ANSWER_OTHER,
  SHUFFLE_NONE,
  SHUFFLE_ALL,
  SHUFFLE_REVERSE,
  SHUFFLE_WITH_LAST_FIXED,
  SHUFFLE_WITH_LAST_TWO_FIXED,
  SHUFFLE_WITH_FIRST_FIXED,
  SHUFFLE_WITH_FIRST_TWO_FIXED,
  SHUFFLE_REVERSE_WITH_LAST_FIXED,
  SHUFFLE_REVERSE_WITH_LAST_TWO_FIXED,
  SHUFFLE_WITH_FIRST_AND_LAST_FIXED,
  SHUFFLE_ROWS_AND_SHUFFLE_COLUMNS_REVERSE,
  SHUFFLE_ROWS,
  SHUFFLE_ROWS_WITH_LAST_FIXED,
  SHUFFLE_ROWS_WITH_LAST_TWO_FIXED,
  SHUFFLE_ROWS_WITH_FIRST_FIXED,
  SHUFFLE_ROWS_WITH_FIRST_TWO_FIXED,
  SHUFFLE_ROWS_REVERSE_WITH_LAST_FIXED,
  SHUFFLE_ROWS_REVERSE_WITH_LAST_TWO_FIXED,
  SHUFFLE_ROWS_REVERSE,
  SHUFFLE_ROWS_WITH_FIRST_AND_LAST_FIXED,
  SHUFFLE_ROWS_REVERSE_WITH_FIRST_FIXED,
  SHUFFLE_ROWS_REVERSE_WITH_FIRST_TWO_FIXED,
  SHUFFLE_COLUMNS,
  SHUFFLE_COLUMNS_REVERSE,
  SHUFFLE_COLUMNS_REVERSE_WITH_LAST_FIXED,
  SHUFFLE_COLUMNS_REVERSE_WITH_LAST_TWO_FIXED,
  SHUFFLE_COLUMNS_WITH_LAST_FIXED,
  SHUFFLE_COLUMNS_WITH_LAST_TWO_FIXED,
  SHUFFLE_COLUMNS_WITH_FIRST_FIXED,
  SHUFFLE_COLUMNS_WITH_FIRST_TWO_FIXED,
  SHUFFLE_COLUMNS_WITH_FIRST_AND_LAST_FIXED,
  SUPPORTED_LANGUAGES,
  BLANK_PIPING_ANSWER,
  GROUP_TYPE_CONJOINT,
  POSTAL_DATA_PREFER_NOT_TO_SAY_ANSWER,
  QUESTION_MATRIX_MAXDIFF,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_DRILLDOWN,
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
  CARRY_FORWARD_ANSWER_OPTIONS,
} from './constants';
import { getOrderedSelectedLevels } from './reactComponents/QuestionBody/DrilldownQuestion/utils';

export const getTimeRemaining = (endTime) => {
  const total = endTime - Date.now();
  const seconds = Math.floor((total / 1000) % 60);
  const minutes = Math.floor((total / 1000 / 60) % 60);
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));
  let text = '00:00';
  if (total > 0) {
    text = `${`0${minutes}`.slice(-2)}:${`0${seconds}`.slice(-2)}`;
  }
  if (total > 0 && days > 0) {
    text = `${days}:${`0${hours}`.slice(-2)}:${`0${minutes}`.slice(
      -2,
    )}:${`0${seconds}`.slice(-2)}`;
  }
  if (total > 0 && days <= 0 && hours > 0) {
    text = `${`0${hours}`.slice(-2)}:${`0${minutes}`.slice(
      -2,
    )}:${`0${seconds}`.slice(-2)}`;
  }

  return {
    total,
    days,
    hours,
    minutes,
    seconds,
    text,
  };
};

export const checkIsPastTimestamp = (timestamp) => {
  if (!timestamp || isNaN(timestamp)) {
    return true;
  }
  return Date.now() >= timestamp;
};

/* eslint-disable no-bitwise */
export const toUTF8Array = function toUTF8Array(str) {
  const utf8 = [];

  for (let i = 0; i < str.length; i += 1) {
    let charcode = str.charCodeAt(i);
    if (charcode < 0x80) utf8.push(charcode);
    else if (charcode < 0x800) {
      utf8.push(0xc0 | (charcode >> 6), 0x80 | (charcode & 0x3f));
    } else if (charcode < 0xd800 || charcode >= 0xe000) {
      utf8.push(
        0xe0 | (charcode >> 12),
        0x80 | ((charcode >> 6) & 0x3f),
        0x80 | (charcode & 0x3f),
      );
    } else {
      i += 1;
      charcode =
        0x10000 + (((charcode & 0x3ff) << 10) | (str.charCodeAt(i) & 0x3ff));
      utf8.push(
        0xf0 | (charcode >> 18),
        0x80 | ((charcode >> 12) & 0x3f),
        0x80 | ((charcode >> 6) & 0x3f),
        0x80 | (charcode & 0x3f),
      );
    }
  }
  return utf8;
};

// NOTE: Some info regarding this function
// We are relying on helper.json.raw.version to tell us what kind of
// android version we are handling here. If for any reason the version that
// comes back is wrong (the backend didn't populate it appropriately) or anything
// related, then we will call the wrong function. So we are fallback handling the error.
// In case the function fails we try calling the older function just to see if that one
// will fail as well and then fallback into an error nevertheless
// I could make this code easier by always trying the newer function first
// (instead of that big ugly if here) but that would generate many false-positives
// when the version indeed knows what happened
export const handleOpenWebsiteInWebview = (
  url,
  surveyByTxt,
  providerName,
  payload,
) => {
  const providerImgPath = `./img/${providerName}.png`;
  // sending extra properties if not supported will crash the android environment
  if (Pollfish.helper.androidSupportsJSONParams()) {
    try {
      Native.openWebsiteInWebview(JSON.stringify(payload));
    } catch (firstError) {
      try {
        Native.openWebsiteInWebview(url, surveyByTxt, providerImgPath);
      } catch (secondError) {
        // if everything fails, report to sentry
        Raven.captureException(secondError, {
          extra: {
            firstError,
            url,
            surveyByTxt,
            providerImgPath,
            payload,
          },
        });
        Pollfish.helper.handleSessionError();
      }
    }
  } else {
    try {
      Native.openWebsiteInWebview(url, surveyByTxt, providerImgPath);
    } catch (firstError) {
      try {
        Native.openWebsiteInWebview(JSON.stringify(payload));
      } catch (secondError) {
        // if everything fails, report to sentry
        Raven.captureException(secondError, {
          extra: {
            firstError,
            url,
            surveyByTxt,
            providerImgPath,
            payload,
          },
        });
        Pollfish.helper.handleSessionError();
      }
    }
  }
};

// There is an issue where an answer is open-ended or other
// the text may contains " which is equal to \" character
// will produce an error in backend due to encoding is being done in mobile,
// example bellow with fail.
// const text = 'Pollfish"';
// const encoded = encodeURIComponent(text);        // 'Pollfish%22'
// const stringified = JSON.stringify(encoded);     // '"Pollfish%22"'
// const decoded = decodeURIComponent(stringified); // '"Pollfish""'
// const parsed = JSON.parse(decoded);              // will produce an error
// We should escape backslash (\) with an extra backslash (\\).
// In order for the backend to not produce an error.
export const escapeJSONSpecialChars = (text) =>
  text
    .replace(/[\\]/g, '\\\\')
    .replace(/["]/g, '\\"')
    .replace(/[\b]/g, '\\b')
    .replace(/[\f]/g, '\\f')
    .replace(/[\n]/g, '\\n')
    .replace(/[\r]/g, '\\r')
    .replace(/[\t]/g, '\\t');

export const encodeTextAnswers = (requestObject) => {
  const { answers } = requestObject;

  return {
    ...requestObject,
    answers: answers.map((answer) => {
      if (answer.text) {
        return {
          ...answer,
          text: encodeURIComponent(escapeJSONSpecialChars(answer.text)),
        };
      }

      // fixes stringValues containing special characters like "&". This fix is needed as long as we send the
      // JSON data format on the url instead of a POST body
      if (answer.matrixColumns) {
        const transformedMatrixColumns = answer.matrixColumns.map(
          (matrixColumn) => {
            if (matrixColumn.stringValue) {
              return {
                ...matrixColumn,
                stringValue: encodeURIComponent(
                  escapeJSONSpecialChars(matrixColumn.stringValue),
                ),
              };
            }
            return matrixColumn;
          },
        );
        return {
          ...answer,
          matrixColumns: transformedMatrixColumns,
        };
      }

      return answer;
    }),
  };
};

// given an object it will transform all values to a string
export const transformValuesToString = (object) => {
  const transformedJSON = {};
  Object.keys(object).forEach((key) => {
    const value = object[key];
    if (isArray(value)) {
      transformedJSON[key] = value;
    } else if (value !== undefined && value !== null) {
      transformedJSON[key] = value.toString();
    }
  });
  return transformedJSON;
};
/* eslint-enable no-bitwise */

export const getRandomBoolean = () => Math.random() >= 0.5;

export const shuffleArray = (
  inputArray,
  shuffleType = SHUFFLE_NONE,
  options = {},
) => {
  const { forceReverse } = options;

  if (shuffleType === SHUFFLE_NONE) {
    return inputArray;
  }

  // we will be using splice further down
  const array = [...inputArray];

  if (
    includes(
      [
        SHUFFLE_REVERSE,
        SHUFFLE_REVERSE_WITH_LAST_FIXED,
        SHUFFLE_REVERSE_WITH_LAST_TWO_FIXED,
        SHUFFLE_ROWS_REVERSE_WITH_FIRST_FIXED,
        SHUFFLE_ROWS_REVERSE_WITH_FIRST_TWO_FIXED,
      ],
      shuffleType,
    )
  ) {
    let shouldReverseBeApplied = Math.random() >= 0.5;

    if (forceReverse === true) {
      shouldReverseBeApplied = true;
    } else if (forceReverse === false) {
      shouldReverseBeApplied = false;
    }

    if (!shouldReverseBeApplied) return array;
  }

  switch (shuffleType) {
    case SHUFFLE_ALL:
      return shuffle(array);
    case SHUFFLE_WITH_LAST_FIXED: {
      const last = array.splice(-1, 1);
      return shuffle(array).concat(last);
    }
    case SHUFFLE_WITH_LAST_TWO_FIXED: {
      const lastTwo = array.splice(-2, 2);
      return shuffle(array).concat(lastTwo);
    }
    case SHUFFLE_REVERSE_WITH_LAST_FIXED: {
      const last = array.splice(-1, 1);
      return array.reverse().concat(last);
    }
    case SHUFFLE_REVERSE_WITH_LAST_TWO_FIXED: {
      const lastTwo = array.splice(-2, 2);
      return array.reverse().concat(lastTwo);
    }
    case SHUFFLE_WITH_FIRST_FIXED: {
      const first = array.splice(0, 1);
      return first.concat(shuffle(array));
    }
    case SHUFFLE_WITH_FIRST_TWO_FIXED: {
      const firstTwo = array.splice(0, 2);
      return firstTwo.concat(shuffle(array));
    }
    case SHUFFLE_WITH_FIRST_AND_LAST_FIXED: {
      const first = array.splice(0, 1);
      const last = array.splice(-1, 1);
      return [...first, ...shuffle(array), ...last];
    }
    case SHUFFLE_ROWS_REVERSE_WITH_FIRST_FIXED: {
      const first = array.splice(0, 1);
      return first.concat(array.reverse());
    }
    case SHUFFLE_ROWS_REVERSE_WITH_FIRST_TWO_FIXED: {
      const firstTwo = array.splice(0, 2);
      return firstTwo.concat(array.reverse());
    }
    case SHUFFLE_REVERSE:
      return array.reverse();
    default:
      return array;
  }
};

export const getScorepointShuffleType = (shuffleType) => {
  const nonShufflingTypes = [
    SHUFFLE_NONE,
    SHUFFLE_ROWS,
    SHUFFLE_ROWS_WITH_LAST_FIXED,
    SHUFFLE_ROWS_WITH_LAST_TWO_FIXED,
    SHUFFLE_ROWS_WITH_FIRST_AND_LAST_FIXED,
    SHUFFLE_ROWS_REVERSE_WITH_LAST_FIXED,
    SHUFFLE_ROWS_REVERSE_WITH_LAST_TWO_FIXED,
    SHUFFLE_ROWS_WITH_FIRST_FIXED,
    SHUFFLE_ROWS_WITH_FIRST_TWO_FIXED,
    SHUFFLE_ROWS_REVERSE,
    SHUFFLE_ROWS_REVERSE_WITH_FIRST_FIXED,
    SHUFFLE_ROWS_REVERSE_WITH_FIRST_TWO_FIXED,
  ];

  if (isUndefined(shuffleType) || nonShufflingTypes.indexOf(shuffleType) > -1) {
    return SHUFFLE_NONE;
  }

  switch (shuffleType) {
    case SHUFFLE_COLUMNS:
      return SHUFFLE_ALL;
    case SHUFFLE_COLUMNS_REVERSE:
      return SHUFFLE_REVERSE;
    case SHUFFLE_COLUMNS_WITH_LAST_FIXED:
      return SHUFFLE_WITH_LAST_FIXED;
    case SHUFFLE_COLUMNS_WITH_LAST_TWO_FIXED:
      return SHUFFLE_WITH_LAST_TWO_FIXED;
    case SHUFFLE_COLUMNS_WITH_FIRST_FIXED:
      return SHUFFLE_WITH_FIRST_FIXED;
    case SHUFFLE_COLUMNS_WITH_FIRST_TWO_FIXED:
      return SHUFFLE_WITH_FIRST_TWO_FIXED;
    case SHUFFLE_COLUMNS_REVERSE_WITH_LAST_FIXED:
      return SHUFFLE_REVERSE_WITH_LAST_FIXED;
    case SHUFFLE_COLUMNS_REVERSE_WITH_LAST_TWO_FIXED:
      return SHUFFLE_REVERSE_WITH_LAST_TWO_FIXED;
    case SHUFFLE_COLUMNS_WITH_FIRST_AND_LAST_FIXED:
      return SHUFFLE_WITH_FIRST_AND_LAST_FIXED;
    case SHUFFLE_ROWS_AND_SHUFFLE_COLUMNS_REVERSE:
      return SHUFFLE_REVERSE;
    default:
      // since we excluded ROWS_ specific shuffles, the default ones apply to all
      return shuffleType;
  }
};

export const getAnswersShuffleType = (shuffleType) => {
  const nonShufflingTypes = [
    SHUFFLE_NONE,
    SHUFFLE_COLUMNS,
    SHUFFLE_COLUMNS_WITH_LAST_FIXED,
    SHUFFLE_COLUMNS_WITH_LAST_TWO_FIXED,
    SHUFFLE_COLUMNS_WITH_FIRST_AND_LAST_FIXED,
    SHUFFLE_COLUMNS_REVERSE_WITH_LAST_FIXED,
    SHUFFLE_COLUMNS_REVERSE_WITH_LAST_TWO_FIXED,
    SHUFFLE_COLUMNS_WITH_FIRST_FIXED,
    SHUFFLE_COLUMNS_WITH_FIRST_TWO_FIXED,
    SHUFFLE_COLUMNS_REVERSE,
  ];

  if (isUndefined(shuffleType) || nonShufflingTypes.indexOf(shuffleType) > -1) {
    return SHUFFLE_NONE;
  }

  switch (shuffleType) {
    case SHUFFLE_ROWS:
      return SHUFFLE_ALL;
    case SHUFFLE_ROWS_REVERSE:
      return SHUFFLE_REVERSE;
    case SHUFFLE_ROWS_WITH_LAST_FIXED:
      return SHUFFLE_WITH_LAST_FIXED;
    case SHUFFLE_ROWS_WITH_LAST_TWO_FIXED:
      return SHUFFLE_WITH_LAST_TWO_FIXED;
    case SHUFFLE_ROWS_WITH_FIRST_FIXED:
      return SHUFFLE_WITH_FIRST_FIXED;
    case SHUFFLE_ROWS_WITH_FIRST_TWO_FIXED:
      return SHUFFLE_WITH_FIRST_TWO_FIXED;
    case SHUFFLE_ROWS_REVERSE_WITH_LAST_FIXED:
      return SHUFFLE_REVERSE_WITH_LAST_FIXED;
    case SHUFFLE_ROWS_REVERSE_WITH_LAST_TWO_FIXED:
      return SHUFFLE_REVERSE_WITH_LAST_TWO_FIXED;
    case SHUFFLE_ROWS_WITH_FIRST_AND_LAST_FIXED:
      return SHUFFLE_WITH_FIRST_AND_LAST_FIXED;
    case SHUFFLE_ROWS_AND_SHUFFLE_COLUMNS_REVERSE:
      return SHUFFLE_ALL;
    default:
      // since we excluded COLUMNS_ specific shuffles, the default ones apply to all
      return shuffleType;
  }
};

export const getLanguageForKey = (key) => {
  const lang = find(SUPPORTED_LANGUAGES, ['key', key]);
  return get(lang, 'value', '');
};

export function isMatrixOpenEndedFamily(type) {
  return includes(
    [
      QUESTION_MATRIX_OPEN_ENDED,
      QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
      QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
    ],
    type,
  );
}

const getCarriedForwardAnswerText = ({ answer, carryForward }) => {
  const {
    relevantRespondentKnowledge,
    // relevantCarryForward,
  } = getRelevantCarryForwardData({ answer, carryForward });
  if (!relevantRespondentKnowledge) return null;

  const {
    answers: relevantRespondentAnswers = [],
  } = relevantRespondentKnowledge;
  const originalRefId =
    answer.originalAnswerRefId || answer.originalScorepointRefId;
  let relevantRespondentAnswer;

  const sourceQuestion = find(Pollfish.survey.getQuestions(), [
    'refId',
    answer.questionRefId,
  ]);
  const sourceQuestionType = get(sourceQuestion, 'style');

  if (
    [
      QUESTION_OPEN_ENDED,
      QUESTION_NUMERICAL_OPEN_ENDED,
      QUESTION_SLIDER,
    ].includes(sourceQuestionType)
  ) {
    // extract first (and single) respondent knowledge answer
    [relevantRespondentAnswer] = relevantRespondentAnswers;
  } else {
    relevantRespondentAnswer = find(relevantRespondentAnswers, [
      'id',
      originalRefId,
    ]);
  }

  if (!relevantRespondentAnswer) return null;

  if (
    answer.isCarriedForward &&
    relevantRespondentAnswer.text !== answer.body
  ) {
    return relevantRespondentAnswer.text;
  }
  return null;
};

const getCarriedForwardScorepointText = ({ scorepoint, carryForward }) => {
  const {
    relevantRespondentKnowledge,
    // relevantCarryForward,
  } = getRelevantCarryForwardData({
    answer: scorepoint,
    carryForward,
  });

  if (!relevantRespondentKnowledge) return null;

  const {
    answers: relevantRespondentAnswers = [],
  } = relevantRespondentKnowledge;
  let relevantRespondentAnswer;
  const sourceQuestion = find(Pollfish.survey.getQuestions(), [
    'refId',
    scorepoint.questionRefId,
  ]);
  const sourceQuestionType = get(sourceQuestion, 'style');

  if (
    [
      QUESTION_OPEN_ENDED,
      QUESTION_NUMERICAL_OPEN_ENDED,
      QUESTION_SLIDER,
    ].includes(sourceQuestionType)
  ) {
    // extract first (and single) respondent knowledge answer
    [relevantRespondentAnswer] = relevantRespondentAnswers;
  } else {
    relevantRespondentAnswer = find(relevantRespondentAnswers, [
      'id',
      scorepoint.originalAnswerRefId,
    ]);
  }

  if (!relevantRespondentAnswer) return null;

  if (
    scorepoint.isCarriedForward &&
    relevantRespondentAnswer.text !== scorepoint.body
  ) {
    return relevantRespondentAnswer.text;
  }
  return null;
};

export function getRelevantCarryForwardData({
  answer = {},
  carryForward = [],
}) {
  const notFound = {
    relevantCarryForward: null,
    relevantRespondentKnowledge: null,
  };

  if (!answer.isCarriedForward) return notFound;

  const relevantCarryForward = find(carryForward, [
    'questionRefId',
    answer.questionRefId,
  ]);

  if (!relevantCarryForward) {
    // If this runs, it means that there are out-of-sync
    // carried forward answers in this question
    // [TODO]: send event to sentry
    return notFound;
  }

  // TODO: Probably remove Pollfish.store reference from here
  const knowledge = Pollfish.store.getRespondentKnowledge();
  const allQuestionAnswers = [
    ...knowledge.questionAnswers,
    ...knowledge.screeningQuestionAnswers,
  ];
  const relevantRespondentKnowledge = find(allQuestionAnswers, [
    'questionId',
    answer.questionRefId,
  ]);

  // if no relevant respondent knowledge, means that the origin
  // question has been skipped and so no answers should be shown
  // from that question
  if (!relevantRespondentKnowledge) return notFound;

  return {
    relevantCarryForward,
    relevantRespondentKnowledge,
  };
}

export function getCarriedForwardAnswers(question) {
  const { carryForward = [] } = question;
  let answers = [...question.answers];

  if (isMatrixOpenEndedFamily(question.style)) {
    // in new matrix schema the carried forward answers
    // exist on the answers array and the rest of the props on the matrixAnswers array
    // so i try to join them here
    const matrixAnswers = get(question, 'matrix.matrixAnswers', []);
    answers = matrixAnswers.map((matrixAnswer) => {
      const relevantAnswer = find(answers, ['id', matrixAnswer.answerId]);
      if (relevantAnswer) {
        return {
          ...matrixAnswer,
          isCarriedForward: relevantAnswer.isCarriedForward,
          questionRefId: relevantAnswer.questionRefId,
          originalAnswerRefId: relevantAnswer.originalAnswerRefId,
        };
      }
      return matrixAnswer;
    });
  }

  if (
    carryForward.length === 0 ||
    !includes(
      [
        QUESTION_SINGLE,
        QUESTION_MULTIPLE,
        QUESTION_RANKING,
        QUESTION_MATRIX_SINGLE,
        QUESTION_MATRIX_MULTIPLE,
        QUESTION_MATRIX_OPEN_ENDED,
        QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
        QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
      ],
      question.style,
    )
  ) {
    return answers;
  }

  return answers.reduce((accumulator, answer) => {
    if (!answer.isCarriedForward) return [...accumulator, answer];

    const {
      relevantRespondentKnowledge,
      relevantCarryForward,
    } = getRelevantCarryForwardData({
      answer,
      carryForward,
    });

    if (!relevantRespondentKnowledge) return accumulator;

    const { answerSelection, answerCompareValue = [] } = relevantCarryForward;
    const { answers: respondentAnswers } = relevantRespondentKnowledge;

    switch (answerSelection) {
      // handle carry from ranking
      case CARRY_FORWARD_ANSWER_OPTIONS.EQUAL_TO:
      case CARRY_FORWARD_ANSWER_OPTIONS.GREATER_THAN:
      case CARRY_FORWARD_ANSWER_OPTIONS.LESSER_THAN:
      case CARRY_FORWARD_ANSWER_OPTIONS.HIGHEST_VALUE:
      case CARRY_FORWARD_ANSWER_OPTIONS.LOWEST_VALUE: {
        const respondentAnswer = find(
          respondentAnswers,
          ({ row }) => row === answer.originalAnswerRefId,
        );
        const isAnswerSelected = Boolean(respondentAnswer);

        if (isAnswerSelected) {
          const userRank = Number(respondentAnswer.column);
          const compareRank = Number(answerCompareValue[0]);
          const maxValue = Math.max(
            ...respondentAnswers.map(({ column }) => Number(column)),
          );
          switch (true) {
            case answerSelection === CARRY_FORWARD_ANSWER_OPTIONS.EQUAL_TO &&
              userRank === compareRank:
            case answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.GREATER_THAN &&
              userRank > compareRank:
            case answerSelection === CARRY_FORWARD_ANSWER_OPTIONS.LESSER_THAN &&
              userRank < compareRank:
            case answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.HIGHEST_VALUE && userRank === 1:
            case answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.LOWEST_VALUE &&
              userRank === maxValue:
              return [...accumulator, answer];
            default:
              break;
          }
        }
        return accumulator;
      }
      // handle carry from matrix columns (in specific rows)
      case CARRY_FORWARD_ANSWER_OPTIONS.SELECTED_FOR_ROW:
      case CARRY_FORWARD_ANSWER_OPTIONS.UNSELECTED_FOR_ROW: {
        // return rows/answers filtered bt user selection
        const respondentAnswer = find(
          respondentAnswers,
          ({ row, column }) =>
            includes(answerCompareValue, row) &&
            column === answer.originalScorepointRefId,
        );

        const isAnswerSelected = Boolean(respondentAnswer);
        const shouldIncludeIt =
          (isAnswerSelected &&
            answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.SELECTED_FOR_ROW) ||
          (!isAnswerSelected &&
            answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.UNSELECTED_FOR_ROW);

        if (shouldIncludeIt) {
          return [...accumulator, answer];
        }
        return accumulator;
      }
      // handle carry from matrix columns
      case CARRY_FORWARD_ANSWER_OPTIONS.SELECTED_COLUMNS:
      case CARRY_FORWARD_ANSWER_OPTIONS.UNSELECTED_COLUMNS: {
        const respondentAnswer = find(
          respondentAnswers,
          ({ column }) =>
            column === answer.originalAnswerRefId ||
            column === answer.originalScorepointRefId,
        );
        const isAnswerSelected = Boolean(respondentAnswer);

        const shouldIncludeIt =
          (isAnswerSelected &&
            answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.SELECTED_COLUMNS) ||
          (!isAnswerSelected &&
            answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.UNSELECTED_COLUMNS);

        if (shouldIncludeIt) {
          const updatedAnswer = { ...answer };
          // update the answers body if the original was dynamic value based
          if (isAnswerSelected && respondentAnswer.text) {
            const text = getDecodedText(respondentAnswer.text);
            updatedAnswer.body = text;
          }
          return [...accumulator, updatedAnswer];
        }
        return accumulator;
      }
      // handle carry from matrix rows
      case CARRY_FORWARD_ANSWER_OPTIONS.SELECTED_FOR_COLUMN:
      case CARRY_FORWARD_ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN: {
        // return rows/answers filtered bt user selection
        const respondentAnswer = find(
          respondentAnswers,
          ({ row, column }) =>
            includes(answerCompareValue, column) &&
            row === answer.originalAnswerRefId,
        );

        const isAnswerSelected = Boolean(respondentAnswer);
        const shouldIncludeIt =
          (isAnswerSelected &&
            answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.SELECTED_FOR_COLUMN) ||
          (!isAnswerSelected &&
            answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN);

        if (shouldIncludeIt) {
          return [...accumulator, answer];
        }
        return accumulator;
      }
      // handle carry from new matrix rows per selected column
      case CARRY_FORWARD_ANSWER_OPTIONS.MATRIX_HIGHEST_VALUE:
      case CARRY_FORWARD_ANSWER_OPTIONS.MATRIX_LOWEST_VALUE:
      case CARRY_FORWARD_ANSWER_OPTIONS.MATRIX_GREATER_THAN:
      case CARRY_FORWARD_ANSWER_OPTIONS.MATRIX_EQUAL_GREATER_THAN:
      case CARRY_FORWARD_ANSWER_OPTIONS.MATRIX_LESSER_THAN:
      case CARRY_FORWARD_ANSWER_OPTIONS.MATRIX_EQUAL_LESSER_THAN: {
        const respondentAnswer = find(
          respondentAnswers,
          ({ row }) => row === answer.originalAnswerRefId,
        );
        const isAnswerSelected = Boolean(respondentAnswer);

        if (isAnswerSelected) {
          const selectedColumnOrder = Number(respondentAnswer.text);
          const compareColumnOrder = Number(answerCompareValue[0]);
          const { text: highestValue } = maxBy(respondentAnswers, 'text');
          const { text: lowestValue } = minBy(respondentAnswers, 'text');

          switch (true) {
            case answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.MATRIX_HIGHEST_VALUE &&
              highestValue === respondentAnswer.text:
            case answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.MATRIX_LOWEST_VALUE &&
              lowestValue === respondentAnswer.text:
            case answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.MATRIX_GREATER_THAN &&
              selectedColumnOrder > compareColumnOrder:
            case answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.MATRIX_EQUAL_GREATER_THAN &&
              selectedColumnOrder >= compareColumnOrder:
            case answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.MATRIX_LESSER_THAN &&
              selectedColumnOrder < compareColumnOrder:
            case answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.MATRIX_EQUAL_LESSER_THAN &&
              selectedColumnOrder <= compareColumnOrder:
              return [...accumulator, answer];
            default:
              break;
          }
        }
        return accumulator;
      }
      // handle carry from new matrix rows
      case CARRY_FORWARD_ANSWER_OPTIONS.MATRIX_EQUAL: {
        // return rows/answers filtered by user selection
        const respondentAnswer = find(
          respondentAnswers,
          ({ row, text }) =>
            includes(answerCompareValue, text) &&
            row === answer.originalAnswerRefId,
        );

        const isAnswerSelected = Boolean(respondentAnswer);

        if (isAnswerSelected) {
          return [...accumulator, answer];
        }
        return accumulator;
      }
      // handle carry from new matrix rows
      // which are not matching some of the selected columns
      case CARRY_FORWARD_ANSWER_OPTIONS.MATRIX_NOT_EQUAL: {
        // return rows/answers filtered by user selection
        const respondentAnswer = find(
          respondentAnswers,
          ({ row, text }) =>
            !includes(answerCompareValue, text) &&
            row === answer.originalAnswerRefId,
        );

        const isAnswerSelected = Boolean(respondentAnswer);

        if (isAnswerSelected) {
          return [...accumulator, answer];
        }
        return accumulator;
      }
      // handle carry selected from open ended, numeric open ended,
      // slider
      case CARRY_FORWARD_ANSWER_OPTIONS.VALUE: {
        const [respondentAnswer] = respondentAnswers;
        const isAnswerSelected = Boolean(respondentAnswer);

        if (isAnswerSelected) {
          const updatedAnswer = { ...answer };
          // update the answers body if the original was dynamic value based
          if (respondentAnswer.text) {
            const text = getDecodedText(respondentAnswer.text);
            if (isMatrixOpenEndedFamily(question.style)) {
              updatedAnswer.beforeLabel = text;
            } else {
              updatedAnswer.body = text;
            }
          }
          return [...accumulator, updatedAnswer];
        }
        return accumulator;
      }
      // handle carry selected from single/multiple selection
      case CARRY_FORWARD_ANSWER_OPTIONS.SELECTED_VALUES: {
        const respondentAnswer = find(
          respondentAnswers,
          ({ id }) =>
            id === answer.originalAnswerRefId ||
            id === answer.originalScorepointRefId,
        );
        const isAnswerSelected = Boolean(respondentAnswer);

        if (isAnswerSelected) {
          const updatedAnswer = { ...answer };
          // update the answers body if the original was dynamic value based
          if (respondentAnswer.text) {
            const text = getDecodedText(respondentAnswer.text);
            if (isMatrixOpenEndedFamily(question.style)) {
              updatedAnswer.beforeLabel = text;
            } else {
              updatedAnswer.body = text;
            }
          }
          return [...accumulator, updatedAnswer];
        }
        return accumulator;
      }
      // handle carry unselected from single/multiple selection
      case CARRY_FORWARD_ANSWER_OPTIONS.UNSELECTED_VALUES: {
        const respondentAnswer = find(
          respondentAnswers,
          ({ id }) =>
            id === answer.originalAnswerRefId ||
            id === answer.originalScorepointRefId,
        );
        const isAnswerSelected = Boolean(respondentAnswer);

        if (!isAnswerSelected) {
          return [...accumulator, answer];
        }
        return accumulator;
      }
      default:
        return accumulator;
    }
  }, []);
}

export function getCarriedForwardScorepoints(question) {
  const { carryForward = [] } = question;
  let scorepoints = [...question.scorepoints];

  if (isMatrixOpenEndedFamily(question.style)) {
    scorepoints = get(question, 'matrix.columns', []);
  }

  if (
    carryForward.length === 0 ||
    !includes(
      [
        QUESTION_MATRIX_SINGLE,
        QUESTION_MATRIX_MULTIPLE,
        QUESTION_MATRIX_OPEN_ENDED,
        QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
        QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
      ],
      question.style,
    )
  ) {
    return scorepoints;
  }

  return scorepoints.reduce((accumulator, scorepoint) => {
    if (!scorepoint.isCarriedForward) return [...accumulator, scorepoint];

    const {
      relevantRespondentKnowledge,
      relevantCarryForward,
    } = getRelevantCarryForwardData({
      answer: scorepoint,
      carryForward,
    });

    if (!relevantRespondentKnowledge) return accumulator;

    const { answerSelection, answerCompareValue } = relevantCarryForward;
    const { answers: respondentAnswers } = relevantRespondentKnowledge;

    switch (answerSelection) {
      case CARRY_FORWARD_ANSWER_OPTIONS.EQUAL_TO:
      case CARRY_FORWARD_ANSWER_OPTIONS.GREATER_THAN:
      case CARRY_FORWARD_ANSWER_OPTIONS.LESSER_THAN:
      case CARRY_FORWARD_ANSWER_OPTIONS.HIGHEST_VALUE:
      case CARRY_FORWARD_ANSWER_OPTIONS.LOWEST_VALUE: {
        const respondentAnswer = find(
          respondentAnswers,
          ({ row }) => row === scorepoint.originalAnswerRefId,
        );
        const isAnswerSelected = Boolean(respondentAnswer);

        if (isAnswerSelected) {
          const userRank = Number(respondentAnswer.column);
          const compareRank = Number(answerCompareValue[0]);
          const maxValue = Math.max(
            ...respondentAnswers.map(({ column }) => Number(column)),
          );
          switch (true) {
            case answerSelection === CARRY_FORWARD_ANSWER_OPTIONS.EQUAL_TO &&
              userRank === compareRank:
            case answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.GREATER_THAN &&
              userRank > compareRank:
            case answerSelection === CARRY_FORWARD_ANSWER_OPTIONS.LESSER_THAN &&
              userRank < compareRank:
            case answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.HIGHEST_VALUE && userRank === 1:
            case answerSelection ===
              CARRY_FORWARD_ANSWER_OPTIONS.LOWEST_VALUE &&
              userRank === maxValue:
              return [...accumulator, scorepoint];
            default:
              break;
          }
        }
        return accumulator;
      }

      case CARRY_FORWARD_ANSWER_OPTIONS.VALUE: {
        const [respondentScorepoint] = respondentAnswers;
        const isScorepointSelected = Boolean(respondentScorepoint);

        if (isScorepointSelected) {
          const updatedAnswer = { ...scorepoint };
          // update the answers body if the original was dynamic value based
          if (respondentScorepoint.text) {
            const text = getDecodedText(respondentScorepoint.text);
            if (isMatrixOpenEndedFamily(question.style)) {
              updatedAnswer.beforeLabel = text;
            } else {
              updatedAnswer.body = text;
            }
          }
          return [...accumulator, updatedAnswer];
        }
        return accumulator;
      }
      case CARRY_FORWARD_ANSWER_OPTIONS.SELECTED_VALUES: {
        const respondentScorepoint = find(
          respondentAnswers,
          ({ id }) =>
            id === scorepoint.originalAnswerRefId ||
            id === scorepoint.originalScorepointRefId,
        );
        const isScorepointSelected = Boolean(respondentScorepoint);

        if (isScorepointSelected) {
          const updatedAnswer = { ...scorepoint };
          // update the answers body if the original was dynamic value based
          if (respondentScorepoint.text) {
            const text = getDecodedText(respondentScorepoint.text);
            if (isMatrixOpenEndedFamily(question.style)) {
              updatedAnswer.beforeLabel = text;
            } else {
              updatedAnswer.body = text;
            }
          }
          return [...accumulator, updatedAnswer];
        }
        return accumulator;
      }

      case CARRY_FORWARD_ANSWER_OPTIONS.UNSELECTED_VALUES: {
        const respondentScorepoint = find(
          respondentAnswers,
          ({ id }) =>
            id === scorepoint.originalAnswerRefId ||
            id === scorepoint.originalScorepointRefId,
        );
        const isScorepointSelected = Boolean(respondentScorepoint);

        if (!isScorepointSelected) {
          return [...accumulator, scorepoint];
        }
        return accumulator;
      }
      default:
        return accumulator;
    }
  }, []);
}
function isMatrixAnswerEmpty(answerResponse) {
  if (isEmpty(answerResponse.matrixColumns)) {
    return true;
  }
  // TODO: we currently only assume booleanType values. This should change in the future
  const isBooleanTypeAnswer = true;
  if (isBooleanTypeAnswer) {
    const isEveryColumnFalse = every(
      answerResponse.matrixColumns,
      (column) => column.booleanValue === false,
    );
    return isEveryColumnFalse;
  }

  // eslint-disable-next-line no-console
  console.warn('Missing implementation');
  return false;
}

// NOTES: this function both validates AND transforms answers due to different needs.
//        I dont think it is the best idea architectural wise to do both but it will be helpful
//        until we have a clear view of what kind of validations and transformations the backend
//        will require for the new matrix.
function validateAndTransformMatrixAnswers(question, answers) {
  const capabilities = get(question, 'matrix.capabilities', {});
  let newAnswers = answers;

  // the backend does not want us to send them empty choices. This is implemented for the MaxDiff
  // question/group type. Be careful if you need to change this to also update maxDiff
  if (capabilities.allowOptionalRows) {
    newAnswers = newAnswers.filter(
      (answerResponse) => !isMatrixAnswerEmpty(answerResponse),
    );
  }

  // not sure how we should handle these type of validations gracefuly on the UI.
  // Sentry is enough for now to inform us in case this happens
  if (capabilities.requireColumnOnce) {
    const columns = get(question, 'matrix.columns', []);
    const requiredColumnIds = columns.map((column) => column.id.toString());
    const selectedColumnIds = [];
    newAnswers.forEach((answerResponse) => {
      if (question.style === QUESTION_DRILLDOWN) {
        answerResponse.matrixColumns.forEach((column) => {
          if (has(column, 'drilldownValue')) {
            selectedColumnIds.push(column.columnId);
          }
        });
      } else {
        const selectedColumn = find(answerResponse.matrixColumns, [
          'booleanValue',
          true,
        ]);
        if (selectedColumn) {
          selectedColumnIds.push(selectedColumn.columnId);
        }
      }
    });

    // we need to sort the arrays because isEqual([columnId1, columndId2], [columnId2, columnId1])
    // will throw an error
    if (!isEqual(sortBy(requiredColumnIds), sortBy(selectedColumnIds))) {
      Raven.captureException(
        'Required columns do not match selected columns for matrix',
        {
          extra: {
            survey_id: Pollfish.helper.json.s_id,
            requiredColumnIds,
            selectedColumnIds,
            question,
            answers,
          },
        },
      );
    }
  }
  return newAnswers;
}

export const transformAnswersForBackendStorage = ({ question, answers }) => {
  const transformedAnswers = [];
  const { carryForward = [] } = question;
  // iterate through all selected answer ids and handle custom question types
  map(answers, (answer, ind) => {
    const answerOrder = findIndex(Pollfish.store.shuffledAnswers, [
      'id',
      answer.id,
    ]);
    const answerResponse = {
      id: answer.id,
    };
    answerResponse.answerOrder = answerOrder.toString();
    if (carryForward.length) {
      const carriedAnswerText = getCarriedForwardAnswerText({
        answer,
        carryForward,
      });

      if (carriedAnswerText) {
        const carriedText = get(answerResponse, 'carriedText', {});
        carriedText.answer = { id: answer.id, text: carriedAnswerText };
        answerResponse.carriedText = carriedText;
      }
    }
    const group = Pollfish.helper.getQuestionGroup(question);
    if (group && group.type === GROUP_TYPE_CONJOINT) {
      // TODO: handle this if/when needed here
    } else if (question.style === QUESTION_OPEN_ENDED) {
      // if it is open-ended handle android sdk < 12 non-latin characters
      // dg_postal_data is mapped to open_ended style
      if (question.f_name === 'dg_postal_data') {
        const selectedPostalCode = Pollfish.helper.getPostalCodeSelection();
        const postalCodeId = selectedPostalCode.attr('data-postal-code-id');
        const postalCodeCountry = selectedPostalCode.attr(
          'data-postal-code-country',
        );
        const postalCodePlaceId = selectedPostalCode.attr(
          'data-postal-code-place-id',
        );
        const selectedPostalCodePreferNotToSay =
          selectedPostalCode.attr('data-postal-code-prefer-not-to-say') ===
          'true';

        if (!selectedPostalCodePreferNotToSay) {
          answerResponse.text = `${postalCodeId}|${postalCodeCountry}|${postalCodePlaceId}`;
        } else {
          answerResponse.text = POSTAL_DATA_PREFER_NOT_TO_SAY_ANSWER;
        }
      } else {
        // get trimmed value of open-ended
        answerResponse.text = Pollfish.helper.getTextFromField('#freeText');
      }
      // if it is numerical open-ended retrive the written value
    } else if (question.style === QUESTION_NUMERICAL_OPEN_ENDED) {
      const value = Pollfish.helper.getNumericValue();
      answerResponse.text = value;
      // if it is ranking get the selected rank
    } else if (question.style === QUESTION_RANKING) {
      const answerElement = $('.answerRanking')[ind];
      const targetId = $(answerElement).data('id');

      const rank = $(answerElement).attr('data-selected-rank');
      answerResponse.id = targetId;
      answerResponse.text = rank;
      // if it is matrix get the selected scorepoint
    } else if (question.style === QUESTION_MATRIX_SINGLE) {
      const answerElement = $(`.answerMatrix[data-answer-id=${answer.id}]`);
      const rowOrder = answerElement.index();
      const selectedColumnId = answerElement.attr(
        'data-selected-scorepoint-id',
      );
      const columnElement = answerElement.find(
        `[data-scorepoint-id=${selectedColumnId}]`,
      );
      const columnOrder = columnElement.index();
      answerResponse.id = answer.id;
      answerResponse.text = selectedColumnId;
      answerResponse.answerOrder = rowOrder.toString();
      answerResponse.textOrder = [columnOrder.toString()];

      if (carryForward.length) {
        const scorepointId = Number(selectedColumnId);

        const { scorepoints = [] } = question;
        const scorepoint = find(scorepoints, ['id', scorepointId]);
        const carriedForwardScorepointText = getCarriedForwardScorepointText({
          scorepoint,
          carryForward,
        });

        if (carriedForwardScorepointText) {
          const carriedText = get(answerResponse, 'carriedText', {});
          const { scorepoints: carriedScorepointsWithText = [] } = carriedText;
          carriedScorepointsWithText.push({
            id: scorepoint.id,
            text: carriedForwardScorepointText,
          });
          carriedText.scorepoints = carriedScorepointsWithText;
          answerResponse.carriedText = carriedText;
        }
      }
    } else if (question.style === QUESTION_MATRIX_MULTIPLE) {
      const answerElement = $(`.answerMatrix[data-answer-id=${answer.id}]`);
      const rowOrder = answerElement.index();
      const selectedScorepointIds = answerElement.attr(
        'data-selected-scorepoint-id',
      );
      const columnOrder = [];
      selectedScorepointIds.split(',').forEach((scorepointId) => {
        const scorepointElement = answerElement.find(
          `[data-scorepoint-id=${scorepointId}]`,
        );
        columnOrder.push(scorepointElement.index().toString());
      });
      answerResponse.id = answer.id;
      answerResponse.text = selectedScorepointIds;
      answerResponse.answerOrder = rowOrder.toString();
      answerResponse.textOrder = columnOrder;

      if (carryForward.length) {
        const scorepointIds = selectedScorepointIds.split(',').map(Number);

        scorepointIds.forEach((scorepointId) => {
          const { scorepoints = [] } = question;
          const scorepoint = find(scorepoints, ['id', scorepointId]);
          const carriedForwardScorepointText = getCarriedForwardScorepointText({
            scorepoint,
            carryForward,
          });

          if (carriedForwardScorepointText) {
            const carriedText = get(answerResponse, 'carriedText', {});
            const {
              scorepoints: carriedScorepointsWithText = [],
            } = carriedText;
            carriedScorepointsWithText.push({
              id: scorepoint.id,
              text: carriedForwardScorepointText,
            });
            carriedText.scorepoints = carriedScorepointsWithText;
            answerResponse.carriedText = carriedText;
          }
        });
      }
    } else if (question.style === QUESTION_SLIDER) {
      // [RES-4943] we send this for d.rules back calculations convenience
      answerResponse.text = answer.body;
    } else if (question.style === QUESTION_NPS) {
      // [RES-4943] we send this for d.rules back calculations convenience
      // parseInt helps handle cases with text e.g. 0 - Not likely
      answerResponse.text = String(parseInt(answer.body, 10));
    } else if (question.style === QUESTION_RATING) {
      // [RES-4943] we send this for d.rules back calculations convenience
      answerResponse.text = answer.body;
    } else if (question.style === QUESTION_MATRIX_VAN_WESTENDORP) {
      const answerElement = $(
        `.answerMatrixVanWestendorp[data-answer-id=${answer.id}]`,
      );
      const answerColumnId = $(answerElement).attr('data-answer-column-id');
      const answerColumnOrder = $(answerElement).attr(
        'data-answer-column-order',
      );
      const answerValue = answerElement.find('.input-van-westendorp').val();
      answerResponse.text = answerValue.toString();
      answerResponse.matrixColumns = [
        {
          columnId: answerColumnId.toString(),
          valueOrder: parseInt(answerColumnOrder, 10),
          numericValue: parseFloat(answerValue),
        },
      ];
    } else if (question.style === QUESTION_MATRIX_MAXDIFF) {
      const answerElement = $(`#${answer.id}.answerClick`);
      const isBestSelection = $(answerElement).data('answer-best') || false;
      const isWorstSelection = $(answerElement).data('answer-worst') || false;

      const columns = get(question, 'matrix.columns');
      const bestColumn = columns[0];
      const worstColumn = columns[1];

      answerResponse.matrixColumns = [];
      answerResponse.matrixColumns.push({
        columnId: bestColumn.id.toString(),
        valueOrder: 0,
        booleanValue: isBestSelection,
      });
      answerResponse.matrixColumns.push({
        columnId: worstColumn.id.toString(),
        valueOrder: 1,
        booleanValue: isWorstSelection,
      });
      let text = answer.body;
      if (isBestSelection) {
        text += `:${bestColumn.beforeLabel}`;
      } else if (isWorstSelection) {
        text += `:${worstColumn.beforeLabel}`;
      }
      answerResponse.text = text;
      // if it is matrix bipolar get the selected column
    } else if (question.style === QUESTION_MATRIX_BIPOLAR) {
      const answerElement = $(`#${answer.id}.answerClick`);
      const answerColumnId = $(answerElement).attr('data-selected-column-id');
      const answerColumnOrder = $(answerElement).attr(
        'data-selected-column-order',
      );
      const columns = get(question, 'matrix.columns', []);
      const column = find(columns, ['id', parseInt(answerColumnId, 10)]);
      answerResponse.matrixColumns = [
        {
          columnId: answerColumnId.toString(),
          valueOrder: parseInt(answerColumnOrder, 10),
          index: column.order,
          booleanValue: true,
        },
      ];

      // check if answer is open-ended and send text as well
    } else if (question.style === QUESTION_DRILLDOWN) {
      // answer.id
      const levels = get(
        question,
        'matrix.validation.drillDownConfiguration.config.levels',
        [],
      );
      const orderedSelectedLevels = getOrderedSelectedLevels(
        levels,
        answer.refId,
      );
      answerResponse.matrixColumns = [];
      question.matrix.columns.forEach((column, index) => {
        const selectedValue = orderedSelectedLevels[index];
        answerResponse.matrixColumns.push({
          columnId: column.id.toString(),
          stringValue: selectedValue.value,
          valueOrder: selectedValue.order,
          drilldownValue: selectedValue.refId,
        });
      });
    } else if (
      question.style === QUESTION_MATRIX_OPEN_ENDED_NUMERIC ||
      question.style === QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM
    ) {
      answerResponse.matrixColumns = [];
      const answerColumns = $(
        `.answer--matrix-open-ended-answer-input[data-row-id=${answer.id}]`,
      );
      each(answerColumns, (domElement) => {
        if ($(domElement).val() !== '') {
          const columnId = $(domElement).attr('data-column-id');
          const columnOrder = $(domElement).attr('data-column-order');
          const answerValue = $(domElement).val();
          answerResponse.matrixColumns.push({
            columnId: columnId.toString(),
            valueOrder: parseInt(columnOrder, 10),
            numericValue: parseFloat(answerValue),
          });
        }
      });
    } else if (answer.style === ANSWER_OTHER) {
      answerResponse.text = Pollfish.helper.getTextFromField(
        '#otherContainerInput',
      );
    }
    // text property is always expected to be a string
    if (!isNil(answerResponse.text) && !isString(answerResponse.text)) {
      answerResponse.text = answerResponse.text.toString();
    }

    transformedAnswers.push(answerResponse);
  });

  const capabilities = get(question, 'matrix.capabilities', {});
  if (!isEmpty(capabilities)) {
    return validateAndTransformMatrixAnswers(question, transformedAnswers);
  }
  return transformedAnswers;
};

export function transformConceptsForBackendStorage(answeredConcepts = []) {
  const attributeGroupingCombinations = [];
  forEach(answeredConcepts, (answeredConcept) => {
    attributeGroupingCombinations.push({
      question_group_ref_id: answeredConcept.nodeId,
      attribute_grouping_ids: answeredConcept.conceptBackEndIds,
    });
  });
  return attributeGroupingCombinations;
}

export const transformAnswersForADL = ({ question, answers }) => {
  const transformedAnswers = [];
  // iterate through all selected answer ids and handle custom question types
  map(answers, (answer) => {
    const answerResponse = {
      id: answer.refId,
    };
    // if it is open-ended handle android sdk < 12 non-latin characters
    if (question.style === QUESTION_OPEN_ENDED) {
      if (question.f_name === 'dg_postal_data') {
        const selectedPostalCode = Pollfish.helper.getPostalCodeSelection();
        const postalCodeId = selectedPostalCode.attr('data-postal-code-id');
        const postalCodeCountry = selectedPostalCode.attr(
          'data-postal-code-country',
        );
        const postalCodePlaceId = selectedPostalCode.attr(
          'data-postal-code-place-id',
        );

        const selectedPostalCodePreferNotToSay =
          selectedPostalCode.attr('data-postal-code-prefer-not-to-say') ===
          'true';

        if (!selectedPostalCodePreferNotToSay) {
          answerResponse.text = `${postalCodeId}|${postalCodeCountry}|${postalCodePlaceId}`;
        } else {
          answerResponse.text = POSTAL_DATA_PREFER_NOT_TO_SAY_ANSWER;
        }
      } else {
        // get trimmed value of open-ended
        answerResponse.text = Pollfish.helper.getTextFromField('#freeText');
      }
      // if it is numerical open-ended retrive the written value
    } else if (question.style === QUESTION_NUMERICAL_OPEN_ENDED) {
      const value = Pollfish.helper.getNumericValue();
      answerResponse.text = value;
      // if it is ranking get the selected rank
    } else if (question.style === QUESTION_RANKING) {
      const answerElement = $(`.answerRanking[data-id="${answer.id}"]`);
      const rank = $(answerElement).attr('data-selected-rank');
      answerResponse.row = answer.refId;
      answerResponse.column = rank;
    } else if (
      question.style === QUESTION_NPS ||
      question.style === QUESTION_SLIDER ||
      question.style === QUESTION_RATING
    ) {
      answerResponse.text = parseInt(answer.body, 10);
      // if it is matrix get the selected scorepoint
    } else if (question.style === QUESTION_MATRIX_SINGLE) {
      const answerElement = $(`.answerMatrix[data-answer-id=${answer.id}]`);
      const selectedColumnTextId = answerElement.attr(
        'data-selected-scorepoint-id',
      );
      const selectedColumnId = parseInt(selectedColumnTextId, 10);
      const column = find(question.scorepoints, ['id', selectedColumnId]);
      answerResponse.row = answer.refId;
      answerResponse.column = column.refId;
    } else if (question.style === QUESTION_MATRIX_MULTIPLE) {
      const answerElement = $(`.answerMatrix[data-answer-id=${answer.id}]`);
      const selectedColumnTextIds = answerElement
        .attr('data-selected-scorepoint-id')
        .split(',');

      // we flat map an answer from { row: 'answer.refId', column: 'scorePoint1, scorepoint2' }
      // to multiple separate answers so that we can evaluate them in our ADL as separate units
      each(selectedColumnTextIds, (scorepointTextId) => {
        const selectedColumnId = parseInt(scorepointTextId, 10);
        const scorepoint = find(question.scorepoints, ['id', selectedColumnId]);
        const multiAnswerResponse = {
          row: answer.refId,
          column: scorepoint.refId,
        };
        transformedAnswers.push(multiAnswerResponse);
      });

      return;
      // check if answer is van westendorp and send text as well
    } else if (question.style === QUESTION_MATRIX_VAN_WESTENDORP) {
      const answerElement = $(
        `.answerMatrixVanWestendorp[data-answer-id=${answer.id}]`,
      );
      const answerColumnId = $(answerElement).attr('data-answer-column-id');
      const answerColumnOrder = $(answerElement).attr(
        'data-answer-column-order',
      );
      const answerValue = answerElement.find('.input-van-westendorp').val();
      answerResponse.text = answerValue.toString();
      answerResponse.matrixColumns = [
        {
          columnId: answerColumnId.toString(),
          valueOrder: parseInt(answerColumnOrder, 10),
          numericValue: parseFloat(answerValue),
        },
      ];
      // if it is matrix bipolar get the selected column
    } else if (question.style === QUESTION_MATRIX_BIPOLAR) {
      const answerElement = $(`#${answer.id}.answerClick`);
      const answerColumnId = $(answerElement).attr('data-selected-column-id');
      const columns = get(question, 'matrix.columns', []);
      const column = find(columns, ['id', parseInt(answerColumnId, 10)]);
      answerResponse.row = answer.refId;
      answerResponse.column = column.refId;
      answerResponse.text = column.order.toString();
      // check if answer is open-ended and send text as well
    } else if (question.style === QUESTION_DRILLDOWN) {
      // answer.id
      const levels = get(
        question,
        'matrix.validation.drillDownConfiguration.config.levels',
        [],
      );
      const orderedSelectedLevels = getOrderedSelectedLevels(
        levels,
        answer.refId,
      );
      question.matrix.columns.forEach((column, index) => {
        const selectedValue = orderedSelectedLevels[index];
        transformedAnswers.push({
          ...answerResponse,
          text: selectedValue.value,
        });
      });
      return;
    } else if (
      question.style === QUESTION_MATRIX_OPEN_ENDED_NUMERIC ||
      question.style === QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM
    ) {
      const answerColumns = $(
        `.answer--matrix-open-ended-answer-input[data-row-id=${answer.id}]`,
      );
      each(answerColumns, (domElement) => {
        if ($(domElement).val() !== '') {
          const rowRefId = $(domElement).attr('data-row-ref-id');
          const columnRefId = $(domElement).attr('data-column-ref-id');
          const answerValue = $(domElement).val();
          transformedAnswers.push({
            row: rowRefId.toString(),
            column: columnRefId.toString(),
            text: answerValue.toString(),
          });
        }
      });
      return;
    } else if (answer.style === ANSWER_OTHER) {
      answerResponse.text = Pollfish.helper.getTextFromField(
        '#otherContainerInput',
      );
    }

    // text property is always expected to be a string
    if (!isNil(answerResponse.text) && !isString(answerResponse.text)) {
      answerResponse.text = answerResponse.text.toString();
    }

    transformedAnswers.push(answerResponse);
  });

  return transformedAnswers;
};

const getAllValuesFromAnswers = (
  style,
  targetedAnswerRefId,
  userAnswers,
  answers,
  scorepoints,
  matrix,
) => {
  const values = [];

  switch (style) {
    case QUESTION_RANKING:
    case QUESTION_MATRIX_SINGLE: {
      const targetedAnswers = filter(
        userAnswers,
        (answer) => answer.row === targetedAnswerRefId || !targetedAnswerRefId,
      );
      each(targetedAnswers, (item) => {
        const { row, column } = item;
        const { body } = find(answers, (answer) => answer.refId === row);

        let selectedColumn = column;
        if (style === QUESTION_MATRIX_SINGLE) {
          ({ body: selectedColumn } =
            find(scorepoints, (scorepoint) => scorepoint.refId === column) ||
            {});
        }

        values.push(`${body}: ${selectedColumn}`);
      });

      break;
    }
    case QUESTION_MATRIX_MULTIPLE: {
      const targetedAnswers = filter(
        userAnswers,
        (answer) => answer.row === targetedAnswerRefId || !targetedAnswerRefId,
      );
      const groupedByRow = groupBy(targetedAnswers, 'row');

      each(keys(groupedByRow), (row) => {
        const { body } = find(answers, (answer) => answer.refId === row);
        const selectedColumns = [];

        each(groupedByRow[row], ({ column }) => {
          const { body: selectedColumn } =
            find(scorepoints, (scorepoint) => scorepoint.refId === column) ||
            {};
          selectedColumns.push(selectedColumn);
        });

        values.push(`${body}: ${selectedColumns.join(', ')}`);
      });

      break;
    }
    case QUESTION_MATRIX_VAN_WESTENDORP: {
      const targetedAnswer = find(
        userAnswers,
        (answer) => answer.id === targetedAnswerRefId,
      );
      if (targetedAnswer) {
        values.push(targetedAnswer.text);
      } else {
        each(userAnswers, (item) => {
          values.push(item.text);
        });
      }

      break;
    }
    case QUESTION_MATRIX_BIPOLAR: {
      const targetedAnswers = filter(
        userAnswers,
        (answer) => answer.row === targetedAnswerRefId || !targetedAnswerRefId,
      );

      each(targetedAnswers, (targetAnswer) => {
        const answer = find(answers, ['refId', targetAnswer.id]) || {};
        const { beforeLabel, afterLabel } =
          find(
            get(matrix, 'matrixAnswers', []),
            (matrixAnswer) => matrixAnswer.answerId === answer.id,
          ) || {};
        const { text = '' } = targetAnswer;
        let fullLabel = beforeLabel;
        if (afterLabel) {
          fullLabel += ` | ${afterLabel}`;
        }
        values.push(`${fullLabel}: ${text}`);
      });

      break;
    }
    default:
      each(userAnswers, (item) => {
        const { id: answerRefId, text } = item;

        // text attribute expected not to be empty string
        if (text) {
          values.push(text);
        } else {
          const { body } = find(
            answers,
            (answer) => answer.refId === answerRefId,
          );
          values.push(body);
        }
      });

      break;
  }

  return values.join(', ');
};

const pipingRegEx = /{{refId:([a-z0-9-]*)(\/subRefId:([a-z0-9-]*))?}}/g;
// transforms prompt based on the selected answer of a previous question
// e.g "Hello {{refId:'questionRefId'}} {{refId:'questionRefId2'}} --> "Hello John Doe"
// we may have to select a specific answer of a previous question based on subRefId
export const transformPrompt = (prompt) => {
  const referencedQuestions = [];
  let newPrompt = prompt;
  let match;

  do {
    match = pipingRegEx.exec(prompt);
    if (match) {
      referencedQuestions.push(match);
    }
  } while (match);

  pipingRegEx.lastIndex = 0;

  if (referencedQuestions.length) {
    const {
      questionAnswers,
      screeningQuestionAnswers,
    } = Pollfish.store.getRespondentKnowledge();
    const allQuestionAnswers = [
      ...questionAnswers,
      ...screeningQuestionAnswers,
    ];

    each(referencedQuestions, (referencedQuestion) => {
      const [textToReplace, refId, , subRefId] = referencedQuestion;
      const {
        answers,
        scorepoints,
        style,
        matrix,
      } = Pollfish.survey.getQuestionByRefId(refId);
      const userAnswered = find(allQuestionAnswers, ['questionId', refId]);

      // when using head_id or head_refId url parameter
      if (!userAnswered) {
        newPrompt = newPrompt.replace(textToReplace, BLANK_PIPING_ANSWER);
      } else {
        const values = getAllValuesFromAnswers(
          style,
          subRefId,
          userAnswered.answers,
          answers,
          scorepoints,
          matrix,
        );
        newPrompt = newPrompt.replace(textToReplace, values);
      }
    });
  }

  // add extra sizing span for emojis
  newPrompt = newPrompt.replace(
    emojiRegex(),
    (emoji) => `<span class="emoji-text">${emoji}</span>`,
  );
  return newPrompt;
};

export function escapeHTML(html = '') {
  const regexEscape = /["&'<>`]/g;
  const escapeMap = {
    '"': '&quot;',
    '&': '&amp;',
    "'": '&#x27;',
    '<': '&lt;',
    '>': '&gt;',
    '`': '&#x60;',
  };
  if (typeof html === 'string') {
    return html.replace(regexEscape, (match) => escapeMap[match]);
  }
  return html;
}

export const cleanAnswersFromUnusedProperties = (answers) =>
  answers.map((answer) =>
    omit(answer, [
      'reward_name',
      'reward_conversion',
      'reward_conversion_hash',
    ]),
  );

// checks if all answers contains only emoji in their body
// answer with emoji separated by space are considered as non only emoji
export const doesAnswersContainsOnlyEmoji = (answers = []) => {
  if (answers.length === 0) {
    return false;
  }
  const answersContainsOnlyEmoji = answers.map((answer) => {
    if (!answer.body) {
      return false;
    }
    const newAnswer = answer.body.replace(emojiRegex(), '');
    return newAnswer.length === 0;
  });
  return answersContainsOnlyEmoji.every((isOnlyEmoji) => isOnlyEmoji);
};

export const roundNumberToStringWithDecimals = (value, decimals = 0) => {
  let output = value;
  output = parseFloat(output);
  output = round(output, decimals);
  return output.toFixed(decimals);
};

export const getTimeDifferenceFromDateInSeconds = (date1, date2) => {
  if (!date2) {
    // eslint-disable-next-line
    date2 = new Date();
  }
  const difference = date2.getTime() - date1.getTime();

  const secondsFromT1ToT2 = difference / 1000;
  return Math.floor(secondsFromT1ToT2);
};

// base64 helper
export function base64() {
  const Base64 = {
    /* eslint-disable */
    _keyStr:
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=',

    encode(input) {
      let output = '';
      let chr1, chr2, chr3, enc1, enc2, enc3, enc4;
      let i = 0;

      input = Base64._utf8_encode(input);

      while (i < input.length) {
        chr1 = input.charCodeAt(i++);
        chr2 = input.charCodeAt(i++);
        chr3 = input.charCodeAt(i++);

        enc1 = chr1 >> 2;
        enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
        enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
        enc4 = chr3 & 63;

        if (isNaN(chr2)) {
          enc3 = enc4 = 64;
        } else if (isNaN(chr3)) {
          enc4 = 64;
        }

        output =
          output +
          this._keyStr.charAt(enc1) +
          this._keyStr.charAt(enc2) +
          this._keyStr.charAt(enc3) +
          this._keyStr.charAt(enc4);
      }

      return output;
    },

    _utf8_encode(string) {
      string = string.replace(/\r\n/g, '\n');
      let utftext = '';

      for (let n = 0; n < string.length; n++) {
        const c = string.charCodeAt(n);

        if (c < 128) {
          utftext += String.fromCharCode(c);
        } else if (c > 127 && c < 2048) {
          utftext += String.fromCharCode((c >> 6) | 192);
          utftext += String.fromCharCode((c & 63) | 128);
        } else {
          utftext += String.fromCharCode((c >> 12) | 224);
          utftext += String.fromCharCode(((c >> 6) & 63) | 128);
          utftext += String.fromCharCode((c & 63) | 128);
        }
      }

      return utftext;
    },

    decode: function(input) {
      if (typeof input !== 'string') return input;
      else input = input.replace(/[^A-Za-z0-9\+\/\=]/g, '');
      let output = '',
        a,
        b,
        c,
        d,
        e,
        f,
        g,
        i = 0;

      while (i < input.length) {
        d = this._keyStr.indexOf(input.charAt(i++));
        e = this._keyStr.indexOf(input.charAt(i++));
        f = this._keyStr.indexOf(input.charAt(i++));
        g = this._keyStr.indexOf(input.charAt(i++));

        a = (d << 2) | (e >> 4);
        b = ((e & 15) << 4) | (f >> 2);
        c = ((f & 3) << 6) | g;

        output += String.fromCharCode(a);
        if (f != 64) output += String.fromCharCode(b);
        if (g != 64) output += String.fromCharCode(c);
      }

      return this._utf8_decode(output);
    },

    _utf8_decode(string) {
      if (typeof string !== 'string') return string;
      let output = '';
      let i = 0;
      let charCode = 0;

      while (i < string.length) {
        charCode = string.charCodeAt(i);

        if (charCode < 128) {
          output += String.fromCharCode(charCode);
          i++;
        } else if (charCode > 191 && charCode < 224) {
          output += String.fromCharCode(
            ((charCode & 31) << 6) | (string.charCodeAt(i + 1) & 63),
          );
          i += 2;
        } else {
          output += String.fromCharCode(
            ((charCode & 15) << 12) |
              ((string.charCodeAt(i + 1) & 63) << 6) |
              (string.charCodeAt(i + 2) & 63),
          );
          i += 3;
        }
      }

      return output;
    },
    /* eslint-enable */
  };

  return Base64;
}
export function getDecodedText(text) {
  if (text.indexOf('BASE64_') === 0) {
    return base64().decode(text.slice(7));
  }
  return text;
}

export const getLocaleDate = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString();
};

export default toUTF8Array;
